import { Fragment } from 'react';
import { useLanguage } from '../i18n';
import { TechDiagram } from '../components/TechDiagram';
import { Reveal } from '../components/Reveal';
import { Overline } from '../components/Overline';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';

const Services = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="services-page" className="pt-16">
      <section className="relative overflow-hidden">
        <TechDiagram
          variant="circuit"
          className="pointer-events-none absolute -right-24 top-10 aspect-square w-[420px] opacity-40"
        />
        <div className="mx-auto max-w-7xl px-6 py-24">
          <PageHero overline={t.services.overline} title={t.services.title} intro={t.services.intro} />
        </div>
      </section>

      <section className="border-t border-graphite-200" data-testid="services-list">
        <div className="mx-auto max-w-7xl px-6">
          {t.services.groups.map((group) => (
            <div key={group.title} className="py-14" data-testid={`service-group-${group.n || group.title}`}>
              <div className="md:grid md:grid-cols-12 md:gap-6">
                <h2 className="text-2xl font-semibold tracking-tight text-silver md:col-span-5">
                  {group.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-steel md:col-span-6 md:col-start-7 md:mt-0">
                  {group.note}
                </p>
              </div>

              <div className="mt-8">
                {group.items.map((s) => (
                  <div
                    key={s.n}
                    className="group grid gap-3 border-t border-graphite-200 py-7 transition-colors duration-200 last:border-b hover:bg-graphite-100/50 md:grid-cols-12 md:items-baseline md:gap-6"
                    data-testid={`service-item-${s.n}`}
                  >
                    <h3 className="text-lg font-medium tracking-tight text-steel-bright transition-colors duration-200 group-hover:text-silver-glow md:col-span-5">
                      {s.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-steel md:col-span-6 md:col-start-7">
                      {s.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-graphite-200" data-testid="services-change">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <Overline>{t.services.change.overline}</Overline>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-steel-bright sm:text-4xl">
              {t.services.change.title}
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-y-10 md:grid-cols-2 md:gap-y-12">
            <p className="hidden text-xs uppercase tracking-wide text-steel-label md:block">
              {t.services.change.todayLabel}
            </p>
            <p className="hidden text-xs uppercase tracking-wide text-steel-label md:block md:border-l md:border-graphite-200 md:pl-16">
              {t.services.change.afterLabel}
            </p>

            {t.services.change.pairs.map((pair, i) => (
              <Fragment key={pair.after}>
                {pair.group && (
                  <p
                    className={`text-sm font-medium tracking-tight text-silver-dim md:col-span-2 ${
                      i > 0 ? 'mt-6 border-t border-graphite-200 pt-12' : ''
                    }`}
                    data-testid={`change-group-${i + 1}`}
                  >
                    {pair.group}
                  </p>
                )}
                <div data-testid={`change-today-${i + 1}`}>
                  <span className="text-xs uppercase tracking-wide text-steel-label md:hidden">
                    {t.services.change.todayLabel}
                  </span>
                  <p className="mt-1 max-w-sm text-lg leading-snug text-steel md:mt-0">
                    {pair.today}
                  </p>
                </div>
                <div
                  className="md:border-l md:border-graphite-200 md:pl-16"
                  data-testid={`change-after-${i + 1}`}
                >
                  <span className="text-xs uppercase tracking-wide text-steel-label md:hidden">
                    {t.services.change.afterLabel}
                  </span>
                  <p className="mt-1 max-w-sm text-lg leading-snug text-silver md:mt-0">
                    {pair.after}
                  </p>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="services-industries">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <Overline>{t.services.industriesOverline}</Overline>
            <h2 className="mt-4 max-w-xl text-3xl font-semibold tracking-tight text-steel-bright sm:text-4xl">
              {t.services.industriesTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-3">
              {t.services.industries.map((ind) => (
                <span
                  key={ind}
                  data-testid={`industry-pill-${ind.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="rounded-full bg-white/[0.05] px-4 py-2 text-sm text-steel transition-colors duration-200 hover:bg-white/[0.09] hover:text-steel-light"
                >
                  {ind}
                </span>
              ))}
            </div>
            <p className="mt-8 max-w-xl text-sm leading-relaxed text-steel-light" data-testid="services-industries-note">
              {t.services.industriesNote}
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection data={t.services.cta} />
    </div>
  );
};

export default Services;
